import React, { useEffect, useState } from "react";
import * as Frigade from "@frigade/react";
import { fetchWithoutAuth } from "../api-client/APIClient";
import { Integration } from "../models/Entities";
import { UserWithBillingPlan } from "../components/context/AppContext";
import { isFrigadeEnabled } from "./FrigadeProvider";

interface FrigadeIntegrationOption {
  value: string;
  label: string;
}

type FrigadeIntegrationState = Record<string, FrigadeIntegrationOption[]> | undefined;

const FREE_GATING_ANNOUNCEMENT_CSS = {
  ".fr-dialog": {
    overflow: "hidden",
    borderRadius: "8px",
    outline: "none",
  },
  ".fr-announcement-header": {
    order: 0,
    textAlign: "left",
  },
  ".fr-image": {
    order: 1,
    borderRadius: "10px 10px 10px 10px",
    aspectRatio: "1.9",
    margin: "-12px 0 0 0",
    minHeight: "238px",
  },
  ".fr-announcement-footer": {
    order: 3,
  },
  ".fr-announcement": {
    maxWidth: "494px",
    paddingBottom: "24px",
  },
};

interface Props {
  user: UserWithBillingPlan;
}

const FrigadeFlows = ({ user }: Props) => {
  // state
  const [categoryToIntegrationMap, setCategoryToIntegrationsMap] =
    useState<FrigadeIntegrationState>(undefined);

  // hooks
  const { addProperties: addFrigadeUserProperties } = Frigade.useUser();

  // effects
  // The questionnaire's follow-up question lists the integrations in the category the user picked.
  useEffect(() => {
    fetchWithoutAuth({
      path: "/integrations",
      method: "GET",
      onResponse: (data: Integration[]) => {
        const integrationMap: FrigadeIntegrationState = {};

        data.forEach((integration: Integration) => {
          const categories = integration.categories;

          categories.forEach((category) => {
            if (!integrationMap[category]) {
              integrationMap[category] = [];
            }

            integrationMap[category].push({
              value: integration.slug,
              label: integration.name,
            });
          });
        });

        setCategoryToIntegrationsMap(integrationMap);
      },
    });
  }, []);

  // render
  return (
    <>
      <Frigade.Form
        as={Frigade.Dialog}
        className="new_customer_questionaire"
        flowId="flow_UD7k8W2gCzkrF7kT"
        dismissible
        onPrimary={(step, event, properties) => {
          if (isFrigadeEnabled(user)) {
            addFrigadeUserProperties({
              keyCategory: properties?.["category"],
              initialIntegration: properties?.["follow-up"],
              role: properties?.["role"],
              implementationTimeframe: properties?.["timeframe"],
              goalWithMerge: properties?.["textMultiline"],
            });
          }

          return true;
        }}
        fieldTypes={{
          DynamicFollowUpBasedOnCategory: (props: Frigade.FormFieldProps) => {
            const categoryValue = props.formContext.watch("category");

            if (!categoryValue) {
              return null;
            }

            const category = categoryValue;
            const categorySpecificData = categoryToIntegrationMap
              ? categoryToIntegrationMap?.[category] ??
                Object.values(categoryToIntegrationMap ?? [])[0]
              : [];

            const initialChoice: FrigadeIntegrationOption[] = [
              {
                value: "i-don't-know-which-integration",
                label: "Not sure which integration",
              },
            ];

            const options = initialChoice.concat(categorySpecificData) ?? [];

            return (
              <Frigade.SelectField
                {...props}
                fieldData={{
                  ...props.fieldData,
                  options,
                }}
              />
            );
          },
        }}
        css={{
          ".fr-form": {
            maxWidth: "500px",
          },
          ".fr-field-label-required": {
            display: "none",
          },
          ".fr-title": {
            fontSize: "20px",
          },
          ".fr-field-select-icon": {
            height: "12px",
            width: "12px",
          },
        }}
      />
      <Frigade.Form
        as={Frigade.Dialog}
        className="more_than_14_days_without_prod_linked_account"
        flowId="flow_fBg5mXrZXKvq8GWX"
        dismissible
      />
      <Frigade.Announcement
        flowId="flow_OyOhal7jhrw2PYbA"
        className="generic_announcement"
        modalPosition="center"
        dismissible
      />
      <Frigade.Announcement flowId="flow_k3M8SUsp" css={FREE_GATING_ANNOUNCEMENT_CSS} dismissible />
      <Frigade.Tour
        key="tour-flow_Zfi4SM02"
        flowId="flow_Zfi4SM02"
        css={{
          transform: "translate(-12px, -21px)",
          ".fr-dot-wrapper": {
            scale: "0.5",
          },
        }}
        avoidCollisions={false}
      />
    </>
  );
};

export default FrigadeFlows;
